import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  KNOWLEDGE_SECTIONS,
  knowledgeLinkSearch,
  knowledgeLocation,
} from "./knowledgeNavigationModel";
import "./knowledgeNavigation.css";
import "./visualsHub.css";

export function KnowledgeNavigation({ canManage = false }: { canManage?: boolean }) {
  const { t } = useTranslation();
  const location = useRouterState({ select: (state) => state.location });
  const current = knowledgeLocation(location.pathname);
  const section = current?.section ?? KNOWLEDGE_SECTIONS[0];
  const items = section.items.filter((item) => !item.admin || canManage);
  const extra = items.filter((item) => item.more);
  const search = location.search as Record<string, unknown>;
  const link = (item: (typeof items)[number]) => (
    <Link
      key={item.id}
      to={item.to as never}
      search={knowledgeLinkSearch(item.to, search) as never}
      activeOptions={{ exact: true, includeSearch: false }}
      aria-current={current?.item.id === item.id ? "page" : undefined}
      data-testid={`knowledge-tab-${item.id}`}
    >
      {t(item.label)}
    </Link>
  );
  // A host-route adapter of the Visualizations hub passes returnTo: the way back is one visible link.
  const back = search.returnTo === "/knowledge/visuals";
  return (
    <div className="knowledge-navigation">
      {back && location.pathname !== "/knowledge/visuals" && (
        <Link className="knowledge-return-to-hub" to={"/knowledge/visuals" as never}
          search={knowledgeLinkSearch("/knowledge/visuals", search) as never} data-testid="knowledge-return-to-hub">
          ← {t("Back to Visualizations")}
        </Link>
      )}
      <nav className="knowledge-sections" aria-label={t("Knowledge sections")}>
        {KNOWLEDGE_SECTIONS.map((group) => (
          <Link
            key={group.id}
            to={group.items[0].to as never}
            search={knowledgeLinkSearch(group.items[0].to, search) as never}
            activeOptions={{ exact: true, includeSearch: false }}
            aria-current={section.id === group.id ? "location" : undefined}
            title={t(group.description)}
          >
            {t(group.label)}
          </Link>
        ))}
      </nav>
      <nav className="knowledge-destinations" aria-label={t("Knowledge views")}>
        {items.filter((item) => !item.more).map(link)}
        {extra.length > 0 && (
          <details className="knowledge-other-views" key={location.pathname}>
            <summary>{current?.item.more ? t(current.item.label) : t("More views")}</summary>
            <div>{extra.map(link)}</div>
          </details>
        )}
      </nav>
    </div>
  );
}
